import { useQuery } from '@tanstack/react-query';
import { directory, isForbidden, schedule } from '@cwos/api-client';
import type { schedule as SCH } from '@cwos/api-client';
import { Pill } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { personDirectory } from '../personDirectory.js';
import { formatClock, formatDay } from '../time/timeLogic.js';
import {
  ASK_STATE_EXPLANATION,
  ASK_STATE_READING,
  RESCHEDULE_READING,
  answerAttribution,
  askStateTone,
  cancelledByReading,
  ATTRIBUTION_LABEL,
  rescheduleStanding,
  reasonReading,
  type AskState,
} from './cancellationLogic.js';

/**
 * WHAT HAPPENED TO THIS SHIFT, beyond the word "cancelled".
 *
 * ---------------------------------------------------------------------------
 * THE READ THIS COULD NOT MAKE UNTIL NOW
 * ---------------------------------------------------------------------------
 * `coverageLogic.ts` carried a note explaining why this section did not exist:
 * `GET /schedule/cancellations/:id` takes a CANCELLATION id, and no surface in
 * the product had one — "a shift knows nothing about its cancellation". The note
 * named the fix (`getCancellationForShift`, written with zero callers) and
 * declined to ship the vocabulary without the read, because a value plumbed
 * through and never read is a defect rather than preparation.
 *
 * `GET /schedule/shifts/:id/cancellation` is that read. This is the vocabulary
 * coming back with it.
 *
 * ---------------------------------------------------------------------------
 * THE STATE THIS SECTION EXISTS TO KEEP APART
 * ---------------------------------------------------------------------------
 * An UNANSWERED ask and an ANSWERED one must never render the same, and neither
 * must an ask that was never put. `askState` is the server's own answer to which
 * of the five it is, and it is never re-derived here: `overdue` and `escalated`
 * are comparisons against `now()`, and the server makes them in the same clock
 * the escalation sweep uses. A browser tab deciding for itself whether a
 * deadline had passed would be a second opinion about what time it is.
 *
 * ---------------------------------------------------------------------------
 * FOUR ANSWERS, NOT ONE EMPTY BOX
 * ---------------------------------------------------------------------------
 *   * `null` from the route — this shift has no cancellation row. On a cancelled
 *     shift that is a genuine finding (the status was set some other way), and
 *     it is SAID rather than left blank.
 *   * 403 — the reader may not reach this worker's roster. A refusal, worded as
 *     one, never as "nothing to show".
 *   * any other failure — a failed read, with a retry.
 *   * a record — rendered.
 */
export function CancellationRecord({
  shiftId,
  /**
   * The `app_user.id` of the worker whose shift this was, when the surface knows
   * it. `null` when the directory has not answered or the reader may not read
   * it — which makes "did somebody answer on their behalf" UNDETERMINED rather
   * than false. Claiming the worker answered when we cannot tell is the same
   * class of false statement as printing a withheld number as a zero.
   */
  workerUserId,
}: {
  shiftId: string;
  workerUserId: string | null;
}) {
  const { has } = usePermissions();
  const canReadDirectory = has('directory:read') || has('user:read');

  const record = useQuery({
    queryKey: ['schedule', 'shift-cancellation', shiftId],
    queryFn: () => schedule.shiftCancellation(shiftId),
  });

  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });

  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );

  return (
    <section className="drawer-section" aria-label="Cancellation">
      <h3 className="drawer-label">Cancellation</h3>

      {record.isError ? (
        isForbidden(record.error) ? (
          <p className="rn-muted" style={{ margin: 0 }}>
            The cancellation record for this shift is not shown to you. That is an answer about your
            reach over this worker’s roster — do not read it as “nothing was recorded”.
          </p>
        ) : (
          <p className="rn-muted" style={{ margin: 0 }}>
            The cancellation record couldn’t be read just now.{' '}
            <button type="button" className="rn-link" onClick={() => void record.refetch()}>
              Try again
            </button>
          </p>
        )
      ) : record.data === undefined ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          Reading the cancellation…
        </p>
      ) : record.data === null ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          This shift is marked cancelled and carries no cancellation record. Nobody was attributed,
          nobody was asked, and no replacement visit is being tracked — this is a gap in the record,
          not a shift that was called off cleanly.
        </p>
      ) : (
        <RecordBody
          view={record.data}
          cancelledBy={cancelledByReading(record.data, people)}
          answer={answerAttribution(record.data, people, workerUserId)}
          escalatedToName={
            record.data.escalatedToUserId === null
              ? null
              : people.name(record.data.escalatedToUserId)
          }
        />
      )}
    </section>
  );
}

function RecordBody({
  view,
  cancelledBy,
  answer,
  escalatedToName,
}: {
  view: SCH.CancellationView;
  cancelledBy: string | null;
  answer: ReturnType<typeof answerAttribution>;
  escalatedToName: string | null;
}) {
  const askState = deriveDisplayState(view);
  const reschedule = rescheduleStanding(view);
  const reason = reasonReading(view.reasonCategory);

  return (
    <div className="drawer-rows">
      <div className="drawer-row">
        <span className="rn-muted">Attributable to</span>
        <span>{ATTRIBUTION_LABEL[view.attributedTo]}</span>
      </div>

      {/* WHICH ACCOUNT — always a row, because "nobody is recorded" is the fact
          the full door exists to make visible. Omitting the row when the column
          is null would render an unattributed cancellation identically to one
          this screen simply did not bother to show. */}
      <div className="drawer-row">
        <span className="rn-muted">Called off by</span>
        <span>
          {cancelledBy ?? (
            <Pill variant="look" dot>
              unattributed
            </Pill>
          )}
        </span>
      </div>

      <div className="drawer-row">
        <span className="rn-muted">Called off</span>
        <span>
          {formatDay(view.cancelledAt)} · {formatClock(view.cancelledAt)}
          {view.sameDay ? <span className="rn-muted"> · same day</span> : null}
        </span>
      </div>

      <div className="drawer-row">
        <span className="rn-muted">The ask</span>
        <Pill variant={askStateTone(askState)} dot>
          {ASK_STATE_READING[askState]}
        </Pill>
      </div>
      <p className="rn-muted" style={{ margin: 0 }}>
        {ASK_STATE_EXPLANATION[askState]}
      </p>

      {view.askDeadlineAt !== null && view.reasonGivenAt === null ? (
        <div className="drawer-row">
          <span className="rn-muted">Answer due</span>
          <span>
            {formatDay(view.askDeadlineAt)} · {formatClock(view.askDeadlineAt)}
          </span>
        </div>
      ) : null}

      {view.escalatedAt !== null ? (
        <div className="drawer-row">
          <span className="rn-muted">Escalated to</span>
          <span>
            {escalatedToName ?? (
              // A REAL ANSWER ABOUT THE ORG CHART, not a failed lookup. The
              // sweep writes null when nobody authority-bearing manages this
              // worker on the day it ran, and a manager reading a blank would
              // think the escalation had gone somewhere.
              <Pill variant="urgent" dot>
                nobody — the org chart names no manager
              </Pill>
            )}
          </span>
        </div>
      ) : null}

      {reason !== null ? (
        <>
          <div className="drawer-row">
            <span className="rn-muted">Reason</span>
            <span>{reason}</span>
          </div>
          {view.reasonNote ? (
            <div className="drawer-row">
              <span className="rn-muted">In their words</span>
              <span>{view.reasonNote}</span>
            </div>
          ) : null}
          {answer !== null ? (
            <div className="drawer-row">
              <span className="rn-muted">Answered by</span>
              <span>
                {answer.answeredByName}
                {answer.onBehalf === true ? (
                  <span className="rn-muted"> · on the worker’s behalf</span>
                ) : answer.onBehalf === null ? (
                  // UNDETERMINED, and said so. The alternative is a screen
                  // implying the worker spoke for themselves when it has no way
                  // to know that.
                  <span className="rn-muted"> · we cannot tell whose account this is</span>
                ) : null}
              </span>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="drawer-row">
        <span className="rn-muted">Replacement visit</span>
        <Pill
          variant={
            reschedule.kind === 'replaced'
              ? 'good'
              : reschedule.kind === 'untracked'
                ? 'urgent'
                : reschedule.kind === 'open'
                  ? 'look'
                  : 'plain'
          }
          dot
        >
          {reschedule.kind === 'replaced'
            ? 'rescheduled'
            : reschedule.kind === 'open'
              ? 'still owed'
              : reschedule.kind === 'untracked'
                ? 'owed, untracked'
                : 'not required'}
        </Pill>
      </div>
      <p className="rn-muted" style={{ margin: 0 }}>
        {RESCHEDULE_READING[reschedule.kind]}
      </p>
    </div>
  );
}

/**
 * The single-record read returns no `askState` — it is a list-shaped field — so
 * the four positions it CAN answer without a clock are derived here, and the
 * fifth is not.
 *
 * `overdue` is deliberately NOT produced by this function. Distinguishing it
 * from `awaiting` requires comparing `askDeadlineAt` against the current instant,
 * and this device's instant is not the one the escalation sweep uses. A record
 * past its deadline therefore reads as `awaiting` here — with the deadline
 * itself rendered beside it, so a reader can see the date has passed — until the
 * sweep moves it to `escalated`. Claiming `overdue` from a browser clock would
 * be a second answer to a question the server already owns.
 */
function deriveDisplayState(view: SCH.CancellationView): AskState {
  if (view.reasonGivenAt !== null) return 'answered';
  if (view.askedAt === null) return 'not_asked';
  if (view.escalatedAt !== null) return 'escalated';
  return 'awaiting';
}
